import type { FC } from 'react'

import type { SxProps, Theme } from '@mui/material'
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material'

import { HOME_CONTENT } from '../defaults'
import { DemoSessionTree } from '@/features/home/demo/components/DemoSessionTree'
import { MobileScreenshotFallback } from './MobileScreenshotFallback'

export const ShowcaseSection: FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  if (isMobile) {
    return (
      <MobileScreenshotFallback
        headingId="showcase-demo-heading"
        title={HOME_CONTENT.showcase.title}
        subtitle={HOME_CONTENT.showcase.subtitle}
        imageSrc={HOME_CONTENT.showcase.image}
        imageAlt={HOME_CONTENT.showcase.imageAlt}
      />
    )
  }

  return (
    <Box
      component="section"
      role="region"
      aria-labelledby="showcase-demo-heading"
      sx={sectionContainerSx}
    >
      <Typography component="h2" id="showcase-demo-heading" sx={sectionTitleSx}>
        {HOME_CONTENT.showcase.title}
      </Typography>
      <Typography sx={sectionSubtitleSx}>{HOME_CONTENT.showcase.subtitle}</Typography>

      <Box sx={demoContainerSx}>
        <DemoSessionTree />
      </Box>
    </Box>
  )
}

const sectionContainerSx: SxProps<Theme> = {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  py: { xs: 4, md: 6 },
  px: { xs: 2, md: 4 },
}

const sectionTitleSx: SxProps<Theme> = {
  color: 'text.primary',
  fontSize: { xs: '1.75rem', md: '2.5rem' },
  fontWeight: 600,
  textAlign: 'center',
  mb: 1,
}

const sectionSubtitleSx: SxProps<Theme> = {
  color: 'text.secondary',
  fontSize: { xs: '1rem', md: '1.1rem' },
  fontWeight: 400,
  textAlign: 'center',
  mb: { xs: 4, md: 6 },
}

const demoContainerSx: SxProps<Theme> = (theme) => ({
  maxWidth: 1000,
  width: '100%',
  borderRadius: 2,
  border:
    theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.08)'
      : '1px solid rgba(62, 39, 35, 0.10)',
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 4px 24px rgba(0, 0, 0, 0.3)'
      : '0 4px 24px rgba(0, 0, 0, 0.08)',
  padding: { xs: 2, md: 3 },
})
